import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { authenticatedApiCall, authenticatedApiCallWithRetry } from '../../utils/apiClient';
import { DEMO_ESTIMATES, getDemoProjects, getDemoRows } from './demoData';
import {
  applySavedTotals,
  getMobileDiscipline,
  normalizeMatrixPayload,
  photoMatchesLine,
  photoTagForLine,
} from './progressCatalog';
import { compressImageFile } from './compressImage';
import {
  enqueuePhoto,
  fileFromQueued,
  loadPhotoQueue,
  photosForRow,
  previewUrlForQueued,
  removeQueuedPhoto,
} from './photoQueue';
import {
  enqueueSave,
  isNetworkError,
  loadSaveQueue,
  readLastJob,
  rememberLastJob,
  removeQueuedSave,
  saveErrorMessage,
  shouldDropFailedPhoto,
  sortJobsForSuperintendent,
} from './saveQueue';

const SAVE_WAIT_MS = 350;

export const useProgressSession = ({ instance, accounts, demo = false }) => {
  const [projects, setProjects] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [rows, setRows] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingEstimates, setLoadingEstimates] = useState(true);
  const [loadingRows, setLoadingRows] = useState(false);
  const [error, setError] = useState('');
  const [saveState, setSaveState] = useState({ status: 'idle', message: '' });
  const [queuedCount, setQueuedCount] = useState(() => (typeof window === 'undefined' ? 0 : loadSaveQueue().length));
  const [queuedPhotoCount, setQueuedPhotoCount] = useState(0);
  const [lastJob, setLastJob] = useState(() => (typeof window === 'undefined' ? null : readLastJob()));
  const [rowPhotos, setRowPhotos] = useState([]);
  const [photoStatus, setPhotoStatus] = useState('');
  const timers = useRef({});
  const demoPhotos = useRef({});
  const lastChange = useRef(null);
  const [canUndo, setCanUndo] = useState(false);

  const bumpUndo = (change) => {
    lastChange.current = change;
    setCanUndo(true);
  };

  const buzz = () => {
    try { window.navigator?.vibrate?.(12); } catch { /* ignore */ }
  };

  const refreshQueued = useCallback(() => {
    setQueuedCount(loadSaveQueue().length);
    loadPhotoQueue().then((items) => setQueuedPhotoCount(items.length)).catch(() => {});
  }, []);

  const loadProjects = useCallback(async () => {
    setLoadingProjects(true);
    setError('');
    try {
      if (demo) {
        setProjects(sortJobsForSuperintendent(getDemoProjects(), readLastJob()));
        return;
      }
      const response = await authenticatedApiCall(instance, accounts, 'GET', '/projects');
      const list = [...(response.data.projects || [])].sort((a, b) => {
        const numA = parseInt(a.job_number, 10);
        const numB = parseInt(b.job_number, 10);
        if (!Number.isNaN(numA) && !Number.isNaN(numB)) return numA - numB;
        return String(a.job_number || '').localeCompare(String(b.job_number || ''));
      });
      setProjects(sortJobsForSuperintendent(list, readLastJob()));
    } catch (err) {
      setError(saveErrorMessage(err, 'Could not load jobs'));
    } finally {
      setLoadingProjects(false);
    }
  }, [accounts, demo, instance]);

  const loadEstimates = useCallback(async () => {
    setLoadingEstimates(true);
    try {
      if (demo) {
        setEstimates(DEMO_ESTIMATES.map((item) => ({ ...item })));
        return;
      }
      const response = await authenticatedApiCall(instance, accounts, 'GET', '/estimates');
      setEstimates(response.data?.estimates || []);
    } catch {
      setEstimates([]);
    } finally {
      setLoadingEstimates(false);
    }
  }, [accounts, demo, instance]);

  const loadRows = useCallback(async (projectId, disciplineId) => {
    const discipline = getMobileDiscipline(disciplineId);
    setLoadingRows(true);
    setError('');
    try {
      if (demo) {
        setRows(getDemoRows(projectId, disciplineId));
        return;
      }
      const endpoint = discipline.config.api.getData.replace('{projectId}', projectId);
      const response = await authenticatedApiCall(instance, accounts, 'GET', endpoint);
      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Could not load tracker');
      }
      setRows(discipline.config.transformData(normalizeMatrixPayload(response.data.data)));
    } catch (err) {
      setRows([]);
      setError(saveErrorMessage(err, 'Could not load tracker'));
    } finally {
      setLoadingRows(false);
    }
  }, [accounts, demo, instance]);

  const persistRow = useCallback(async (projectId, disciplineId, rowId, changes, options = {}) => {
    const discipline = getMobileDiscipline(disciplineId);
    if (demo) {
      setSaveState({ status: 'saved', message: 'Saved on this phone (preview)' });
      return true;
    }
    const payloads = Object.entries(changes)
      .map(([columnId, { oldValue, newValue }]) =>
        discipline.config.buildChangePayload(rowId, columnId, oldValue, newValue))
      .filter(Boolean);
    if (!payloads.length) return true;

    setSaveState({ status: 'saving', message: 'Saving…' });
    try {
      const response = await authenticatedApiCallWithRetry(
        instance,
        accounts,
        'POST',
        discipline.config.api.saveChanges,
        { project_id: projectId, changes: payloads },
        {},
        { maxRetries: 3, baseDelayMs: 500, maxDelayMs: 3000 }
      );
      const respData = response.data?.data;
      const savedRows = Array.isArray(respData?.updatedRows)
        ? respData.updatedRows
        : (Array.isArray(respData) ? respData : []);
      const saved = savedRows.find((item) => String(item.id) === String(rowId)) || savedRows[0];
      if (saved) {
        setRows((current) => current.map((row) => (
          String(row.id) === String(rowId) ? applySavedTotals(row, saved) : row
        )));
      }
      Object.keys(changes).forEach((columnId) => {
        removeQueuedSave({ projectId, disciplineId, rowId, columnId });
      });
      refreshQueued();
      setSaveState({ status: 'saved', message: 'Saved' });
      buzz();
      return true;
    } catch (err) {
      if (!options.fromQueue && isNetworkError(err)) {
        Object.entries(changes).forEach(([columnId, value]) => {
          enqueueSave({
            projectId,
            disciplineId,
            rowId,
            columnId,
            oldValue: value.oldValue,
            newValue: value.newValue,
          });
        });
        refreshQueued();
        setSaveState({
          status: 'queued',
          message: `No signal — saved on this phone, will send (${loadSaveQueue().length})`,
        });
        return false;
      }
      setSaveState({
        status: 'error',
        message: saveErrorMessage(err, 'Could not save — tap the step again'),
      });
      return false;
    }
  }, [accounts, demo, instance, refreshQueued]);

  const updateStep = useCallback((projectId, disciplineId, rowId, columnId, newValue) => {
    let oldValue = 0;
    setRows((current) => current.map((row) => {
      if (String(row.id) !== String(rowId)) return row;
      oldValue = row[columnId];
      return { ...row, [columnId]: newValue };
    }));
    bumpUndo({
      projectId, disciplineId, rowId, columnId, oldValue, newValue, kind: 'step',
    });
    const key = `${rowId}:${columnId}`;
    if (timers.current[key]) clearTimeout(timers.current[key]);
    timers.current[key] = setTimeout(() => {
      persistRow(projectId, disciplineId, rowId, {
        [columnId]: { oldValue, newValue },
      });
    }, SAVE_WAIT_MS);
  }, [persistRow]);

  const undoLast = useCallback(() => {
    const change = lastChange.current;
    if (!change) return;
    lastChange.current = null;
    setCanUndo(false);
    if (change.kind === 'hold') {
      setRows((current) => current.map((row) => (
        String(row.id) === String(change.rowId) ? { ...row, onHold: change.oldValue } : row
      )));
      persistRow(change.projectId, change.disciplineId, change.rowId, {
        onHold: { oldValue: change.newValue, newValue: change.oldValue },
      });
      return;
    }
    setRows((current) => current.map((row) => (
      String(row.id) === String(change.rowId) ? { ...row, [change.columnId]: change.oldValue } : row
    )));
    persistRow(change.projectId, change.disciplineId, change.rowId, {
      [change.columnId]: { oldValue: change.newValue, newValue: change.oldValue },
    });
  }, [persistRow]);

  const updateHold = useCallback((projectId, disciplineId, rowId, onHold) => {
    let oldValue = false;
    setRows((current) => current.map((row) => {
      if (String(row.id) !== String(rowId)) return row;
      oldValue = !!row.onHold;
      return { ...row, onHold };
    }));
    persistRow(projectId, disciplineId, rowId, {
      onHold: { oldValue, newValue: onHold },
    });
    bumpUndo({
      projectId, disciplineId, rowId, columnId: 'onHold', oldValue, newValue: onHold, kind: 'hold',
    });
  }, [persistRow]);

  const updateComment = useCallback((projectId, disciplineId, rowId, comments) => {
    let oldValue = '';
    setRows((current) => current.map((row) => {
      if (String(row.id) !== String(rowId)) return row;
      oldValue = row.comments || '';
      return { ...row, comments };
    }));
    if (timers.current[`c:${rowId}`]) clearTimeout(timers.current[`c:${rowId}`]);
    timers.current[`c:${rowId}`] = setTimeout(() => {
      persistRow(projectId, disciplineId, rowId, {
        comments: { oldValue, newValue: comments },
      });
    }, 700);
  }, [persistRow]);

  const pinJob = useCallback((project) => {
    rememberLastJob(project);
    setLastJob(readLastJob());
    setProjects((current) => sortJobsForSuperintendent(current, readLastJob()));
  }, []);

  const localPhotosForRow = useCallback(async (projectId, disciplineId, row) => {
    const pending = await photosForRow(projectId, disciplineId, row?.id);
    return pending.map((item) => ({
      id: item.id,
      title: item.tag?.title || 'Queued photo',
      previewUrl: previewUrlForQueued(item),
      queued: true,
    }));
  }, []);

  const loadPhotos = useCallback(async (projectId, disciplineId, row) => {
    const discipline = getMobileDiscipline(disciplineId);
    const key = `${projectId}:${disciplineId}:${row?.id}`;
    if (demo) {
      setRowPhotos(demoPhotos.current[key] || []);
      return;
    }
    if (!projectId || !row) {
      setRowPhotos([]);
      return;
    }
    const local = await localPhotosForRow(projectId, disciplineId, row);
    try {
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        `/technical-photos/project/${projectId}`
      );
      const all = response.data?.data || [];
      setRowPhotos([
        ...local,
        ...all.filter((photo) => photoMatchesLine(photo, discipline, row)),
      ]);
    } catch {
      setRowPhotos(local);
    }
  }, [accounts, demo, instance, localPhotosForRow]);

  const sendPhoto = useCallback(async (projectId, disciplineId, row, file, tag, locationOverride) => {
    const formData = new FormData();
    formData.append('photos', file);
    formData.append('title', tag.title);
    formData.append('description', tag.description);
    formData.append('category', tag.category);
    const location = locationOverride || lineMetaSafe(row);
    if (location) formData.append('location', location);
    const uploadRes = await authenticatedApiCall(
      instance,
      accounts,
      'POST',
      `/technical-photos/project/${projectId}/upload`,
      formData
    );
    const created = Array.isArray(uploadRes.data?.data) ? uploadRes.data.data : [uploadRes.data?.data];
    for (const photo of created.filter(Boolean)) {
      await authenticatedApiCall(instance, accounts, 'POST', `/technical-photos/${photo.id}/tags`, {
        x_coordinate: -1,
        y_coordinate: -1,
        linked_item_type: tag.linked_item_type,
        linked_item_id: tag.linked_item_id,
        description: tag.description,
      });
    }
  }, [accounts, instance]);

  const uploadPhoto = useCallback(async (projectId, disciplineId, row, file) => {
    const discipline = getMobileDiscipline(disciplineId);
    const tag = photoTagForLine(discipline, row);
    const key = `${projectId}:${disciplineId}:${row?.id}`;
    setPhotoStatus('Uploading photo…');
    const outbound = await compressImageFile(file);
    if (demo) {
      const preview = {
        id: `demo-${Date.now()}`,
        title: tag.title,
        previewUrl: URL.createObjectURL(outbound),
        demo: true,
      };
      demoPhotos.current[key] = [...(demoPhotos.current[key] || []), preview];
      setRowPhotos(demoPhotos.current[key]);
      setPhotoStatus('Photo kept on this phone (preview)');
      return;
    }
    try {
      await sendPhoto(projectId, disciplineId, row, outbound, tag);
      setPhotoStatus('Photo saved under Technical → Photos');
      await loadPhotos(projectId, disciplineId, row);
    } catch (err) {
      if (isNetworkError(err)) {
        await enqueuePhoto({
          projectId,
          disciplineId,
          rowId: row.id,
          file: outbound,
          tag,
          location: lineMetaSafe(row),
        });
        refreshQueued();
        setPhotoStatus('No signal — photo kept on this phone, will send');
        await loadPhotos(projectId, disciplineId, row);
        return;
      }
      setPhotoStatus(saveErrorMessage(err, 'Photo did not send — try again when signal is back'));
    }
  }, [demo, loadPhotos, refreshQueued, sendPhoto]);

  const flushPhotos = useCallback(async () => {
    if (demo) return;
    const items = await loadPhotoQueue();
    for (const item of items) {
      try {
        await sendPhoto(
          item.projectId,
          item.disciplineId,
          { id: item.rowId },
          fileFromQueued(item),
          item.tag,
          item.location
        );
        await removeQueuedPhoto(item.id);
      } catch (err) {
        if (isNetworkError(err)) break;
        if (shouldDropFailedPhoto(err)) {
          await removeQueuedPhoto(item.id);
          continue;
        }
        break;
      }
    }
    refreshQueued();
  }, [demo, refreshQueued, sendPhoto]);

  const flushQueue = useCallback(async () => {
    if (demo) return;
    const items = loadSaveQueue();
    for (const item of items) {
      const ok = await persistRow(
        item.projectId,
        item.disciplineId,
        item.rowId,
        { [item.columnId]: { oldValue: item.oldValue, newValue: item.newValue } },
        { fromQueue: true }
      );
      if (!ok) break;
    }
    await flushPhotos();
    refreshQueued();
  }, [demo, flushPhotos, persistRow, refreshQueued]);

  useEffect(() => {
    const onOnline = () => { flushQueue(); };
    window.addEventListener('online', onOnline);
    flushQueue();
    return () => window.removeEventListener('online', onOnline);
  }, [flushQueue]);

  useEffect(() => () => {
    Object.values(timers.current).forEach((timer) => clearTimeout(timer));
  }, []);

  return useMemo(() => ({
    projects,
    estimates,
    rows,
    loadingProjects,
    loadingEstimates,
    loadingRows,
    error,
    saveState,
    queuedCount,
    queuedPhotoCount,
    lastJob,
    rowPhotos,
    photoStatus,
    canUndo,
    loadProjects,
    loadEstimates,
    loadRows,
    loadPhotos,
    uploadPhoto,
    updateStep,
    updateComment,
    updateHold,
    undoLast,
    pinJob,
    flushQueue,
  }), [
    canUndo,
    error,
    flushQueue,
    estimates,
    lastJob,
    loadEstimates,
    loadPhotos,
    loadProjects,
    loadRows,
    loadingEstimates,
    loadingProjects,
    loadingRows,
    photoStatus,
    pinJob,
    projects,
    queuedCount,
    queuedPhotoCount,
    rowPhotos,
    rows,
    saveState,
    undoLast,
    updateComment,
    updateHold,
    updateStep,
    uploadPhoto,
  ]);
};

const lineMetaSafe = (row) =>
  [row?.area, row?.subArea, row?.buildingNumber].filter(Boolean).join(' · ');
